import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast } from "@/components/system";
import {
  Row,
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  localNowDatetime,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * Quick-log mood (SPEC 3.1.4). Writes one row to `subjective_check_in`.
 *
 * The 5-button mood strip drives `mood`; energy/motivation/soreness scales
 * are 0–10 numerics. prompt_kind auto-derives from time-of-day:
 *   <12:00 → 'morning', ≥18:00 → 'evening', else 'ad_hoc'.
 */

const MOODS: { value: "flat" | "low" | "neutral" | "good" | "high"; label: string }[] = [
  { value: "flat", label: "Flat" },
  { value: "low", label: "Low" },
  { value: "neutral", label: "Neutral" },
  { value: "good", label: "Good" },
  { value: "high", label: "High" },
];

function defaultPromptKind(): "morning" | "evening" | "ad_hoc" {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h >= 18) return "evening";
  return "ad_hoc";
}

export function LogMoodDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [mood, setMood] = useState<typeof MOODS[number]["value"]>("neutral");
  const [energy, setEnergy] = useState("");
  const [motivation, setMotivation] = useState("");
  const [sorenessOverall, setSorenessOverall] = useState("");
  const [promptKind, setPromptKind] = useState(defaultPromptKind());
  const [notes, setNotes] = useState("");
  const [ts, setTs] = useState(localNowDatetime());

  const mutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("subjective_check_in")
        .insert({
          user_id: userId,
          ts: new Date(ts).toISOString(),
          tz: browserTz(),
          prompt_kind: promptKind,
          mood,
          energy: energy ? Number(energy) : null,
          motivation: motivation ? Number(motivation) : null,
          soreness_overall: sorenessOverall ? Number(sorenessOverall) : null,
          notes,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["check_ins", userId] });
      toast.show(`Mood logged: ${mood}.`);
      reset();
      onClose();
    },
    onError: (e) => toast.show(`Couldn't log mood: ${formatError(e)}`),
  });

  function reset() {
    setMood("neutral");
    setEnergy("");
    setMotivation("");
    setSorenessOverall("");
    setPromptKind(defaultPromptKind());
    setNotes("");
    setTs(localNowDatetime());
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Log mood"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        <fieldset
          style={{
            border: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2)",
          }}
        >
          <legend
            style={{
              padding: 0,
              fontSize: "var(--type-body-s)",
              color: "var(--fg-2)",
              marginBottom: "var(--space-1)",
            }}
          >
            How do you feel?
          </legend>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${MOODS.length}, 1fr)`,
              gap: "var(--space-2)",
            }}
            role="radiogroup"
            aria-label="Mood"
          >
            {MOODS.map((m) => {
              const active = m.value === mood;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(m.value)}
                  role="radio"
                  aria-checked={active}
                  style={{
                    height: 38,
                    borderRadius: "var(--radius-card)",
                    border: `1px solid ${active ? "var(--accent)" : "var(--hair-2)"}`,
                    background: active ? "var(--accent-wash)" : "transparent",
                    color: active ? "var(--fg)" : "var(--fg-2)",
                    fontSize: "var(--type-body-s)",
                    fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <Row>
          <FormInput
            label="Energy 0–10"
            inputMode="numeric"
            value={energy}
            onChange={setEnergy}
            placeholder="6"
            min={0}
            max={10}
          />
          <FormInput
            label="Motivation 0–10"
            inputMode="numeric"
            value={motivation}
            onChange={setMotivation}
            placeholder="5"
            min={0}
            max={10}
          />
          <FormInput
            label="Soreness 0–10"
            inputMode="numeric"
            value={sorenessOverall}
            onChange={setSorenessOverall}
            placeholder="2"
            min={0}
            max={10}
          />
        </Row>

        <Row>
          <FormSelect
            label="Prompt"
            value={promptKind}
            onChange={(v) =>
              setPromptKind(v as "morning" | "evening" | "ad_hoc")
            }
            options={[
              { value: "morning", label: "Morning" },
              { value: "evening", label: "Evening" },
              { value: "pre_workout", label: "Pre workout" },
              { value: "post_workout", label: "Post workout" },
              { value: "ad_hoc", label: "Ad-hoc" },
            ]}
          />
          <FormInput
            label="When"
            type="datetime-local"
            value={ts}
            onChange={setTs}
          />
        </Row>

        <FormTextarea
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          placeholder="…"
        />

        <DialogFooter>
          <SecondaryButton
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={mutation.isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Log mood"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}
